"use client";

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface InterlinkRule {
  _id: string;
  pagePath: string;
  selectedText: string;
  contextBefore: string;
  contextAfter: string;
  url: string;
  rel?: string | null; // Link rel attribute: 'nofollow' or null (dofollow)
  isActive: boolean;
}

/**
 * Global component that handles:
 * 1. Text selection mode (when ?interlinkingMode=1) - sends postMessage to admin
 * 2. Link injection on live pages - applies interlinks from MongoDB
 */
export default function InterlinkingInjector() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInterlinkingMode = searchParams.get('interlinkingMode') === '1';
  const isAdminRoute = pathname.startsWith('/admin');
  const [rules, setRules] = useState<InterlinkRule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewRefreshTrigger, setPreviewRefreshTrigger] = useState(0);
  const injectedRef = useRef(false);
  const selectionListenerRef = useRef<(() => void) | null>(null);

  // Listen for parent (dashboard) to tell us rules changed — refresh preview without reload
  useEffect(() => {
    if (!isInterlinkingMode || isAdminRoute) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'INTERLINK_RULES_UPDATED') {
        setPreviewRefreshTrigger((prev) => prev + 1);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isInterlinkingMode, isAdminRoute]);

  // Mixed-case path should have been redirected by middleware/script; redirect here and skip API calls
  useEffect(() => {
    if (pathname.startsWith('/api') || pathname.startsWith('/_next')) return;
    if (!/[A-Z]/.test(pathname)) return;
    const lower = pathname.toLowerCase();
    if (pathname === lower) return;
    if (typeof window !== 'undefined') {
      window.location.replace(lower + window.location.search + window.location.hash);
    }
  }, [pathname]);

  // Fetch interlink rules for current page (only for canonical lowercase paths)
  useEffect(() => {
    if (isAdminRoute || isInterlinkingMode) return;
    if (/[A-Z]/.test(pathname)) return; // Skip until redirected to lowercase

    const fetchRules = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/interlinking?path=${encodeURIComponent(pathname)}`);
        if (response.ok) {
          const data = await response.json();
          setRules(data.rules || []);
        }
      } catch (error) {
        console.error('Error fetching interlink rules:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRules();
  }, [pathname, isAdminRoute, isInterlinkingMode]);

  // Inject links into DOM for frontend pages (no highlighting)
  useEffect(() => {
    if (isAdminRoute || isInterlinkingMode || isLoading || rules.length === 0) {
      return;
    }

    const injectLinks = () => {
      try {
        const main = document.querySelector('main');
        if (!main) return;

        // Helper function to get all text nodes
        const getAllTextNodes = () => {
          const walker = document.createTreeWalker(
            main!,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) => {
                const parent = node.parentElement;
                if (!parent) return NodeFilter.FILTER_REJECT;
                if (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
                  return NodeFilter.FILTER_REJECT;
                }
                if (parent.closest('a') || parent.closest('.interlink-link')) {
                  return NodeFilter.FILTER_REJECT;
                }
                // Allow interlinking inside FAQ (questions and answers) even when question is in a button
                if (parent.closest('[data-interlink-allow="faq"]')) {
                  return NodeFilter.FILTER_ACCEPT;
                }
                if (parent.closest('button') || parent.closest('[role="button"]') || parent.closest('[role="link"]')) {
                  return NodeFilter.FILTER_REJECT;
                }
                let checkParent: Element | null = parent;
                while (checkParent && checkParent !== main) {
                  if (checkParent.tagName === 'A' || checkParent.hasAttribute('href')) {
                    return NodeFilter.FILTER_REJECT;
                  }
                  checkParent = checkParent.parentElement;
                }
                return NodeFilter.FILTER_ACCEPT;
              },
            }
          );

          const textNodes: { node: Text; text: string; parent: Node }[] = [];
          let node: Text | null;
          while ((node = walker.nextNode() as Text | null)) {
            if (node && node.textContent) {
              textNodes.push({
                node,
                text: node.textContent,
                parent: node.parentNode!,
              });
            }
          }
          return textNodes;
        };

        // Helper function to find all occurrences of a pattern
        const findAllOccurrences = (text: string, pattern: string): number[] => {
          const occurrences: number[] = [];
          let index = text.indexOf(pattern);
          while (index !== -1) {
            occurrences.push(index);
            index = text.indexOf(pattern, index + 1);
          }
          return occurrences;
        };

        // Process each rule and find all matches
        const activeRules = rules.filter(rule => rule.isActive);
        if (activeRules.length === 0) return;

        let hasChanges = false;
        let iteration = 0;
        const maxIterations = 10; // Prevent infinite loops

        while (iteration < maxIterations) {
          const textNodes = getAllTextNodes();
          if (textNodes.length === 0) break;

          // Build text map
          let fullText = '';
          const nodeMap: Array<{ node: Text; start: number; end: number }> = [];
          textNodes.forEach(({ node, text }) => {
            const start = fullText.length;
            fullText += text;
            const end = fullText.length;
            nodeMap.push({ node, start, end });
          });

          let foundAny = false;

          // Process rules - find all matches for each rule
          for (const rule of activeRules) {
            let positions: number[] = [];

            // Check if context is provided
            const hasContextBefore = rule.contextBefore && rule.contextBefore.trim();
            const hasContextAfter = rule.contextAfter && rule.contextAfter.trim();
            const hasContext = hasContextBefore || hasContextAfter;
            
            if (hasContext) {
              // Build search pattern - account for potential whitespace between parts
              // We'll search for selectedText and verify context around it
              const selectedTextPositions = findAllOccurrences(fullText, rule.selectedText);
              
              // Filter positions where context matches
              for (const pos of selectedTextPositions) {
                const selectedTextEnd = pos + rule.selectedText.length;
                let contextMatches = true;
                
                // Check contextBefore (allow whitespace before contextBefore)
                if (hasContextBefore) {
                  // Look for contextBefore within reasonable distance before selectedText (up to 10 chars for whitespace)
                  const searchStart = Math.max(0, pos - rule.contextBefore.length - 10);
                  const textBeforeSelected = fullText.substring(searchStart, pos);
                  
                  // Try exact match first
                  let contextBeforeIndex = textBeforeSelected.lastIndexOf(rule.contextBefore);
                  
                  // If not found, try trimmed match (allows for whitespace)
                  if (contextBeforeIndex === -1) {
                    const trimmedBefore = textBeforeSelected.trim();
                    const trimmedContextBefore = rule.contextBefore.trim();
                    const trimmedIndex = trimmedBefore.lastIndexOf(trimmedContextBefore);
                    if (trimmedIndex !== -1) {
                      // Map back to original position accounting for trimmed whitespace
                      const beforeTrimmed = textBeforeSelected.substring(0, textBeforeSelected.indexOf(trimmedBefore));
                      contextBeforeIndex = beforeTrimmed.length + trimmedIndex;
                    }
                  }
                  
                  // Check if contextBefore is found and ends close to selectedText
                  if (contextBeforeIndex === -1) {
                    contextMatches = false;
                  } else {
                    const contextBeforeEnd = searchStart + contextBeforeIndex + rule.contextBefore.length;
                    // Allow up to 10 characters of whitespace/punctuation between contextBefore and selectedText
                    if (pos - contextBeforeEnd > 10) {
                      contextMatches = false;
                    }
                  }
                }
                
                // Check contextAfter (allow whitespace between selectedText and contextAfter)
                if (hasContextAfter && contextMatches) {
                  // Look for contextAfter within reasonable distance (up to 15 chars for whitespace/punctuation)
                  const searchEnd = Math.min(selectedTextEnd + rule.contextAfter.length + 15, fullText.length);
                  const textAfterSelected = fullText.substring(selectedTextEnd, searchEnd);
                  
                  // Try exact match first
                  let contextAfterIndex = textAfterSelected.indexOf(rule.contextAfter);
                  
                  // If not found, try trimmed match (allows for whitespace)
                  if (contextAfterIndex === -1) {
                    const trimmedAfter = textAfterSelected.trim();
                    const trimmedContextAfter = rule.contextAfter.trim();
                    const trimmedIndex = trimmedAfter.indexOf(trimmedContextAfter);
                    if (trimmedIndex !== -1 && trimmedIndex <= 10) {
                      // Find the position in original text accounting for trimmed whitespace
                      const beforeTrimmed = textAfterSelected.substring(0, textAfterSelected.indexOf(trimmedAfter));
                      contextAfterIndex = beforeTrimmed.length + trimmedIndex;
                    }
                  }
                  
                  // For very short contextAfter (like "?"), be more lenient
                  const maxDistance = rule.contextAfter.length <= 2 ? 5 : 15;
                  
                  // Check if contextAfter is found within reasonable distance
                  if (contextAfterIndex === -1 || contextAfterIndex > maxDistance) {
                    contextMatches = false;
                  }
                }
                
                if (contextMatches) {
                  positions.push(pos);
                }
              }
              
              // If no matches found with context, skip this rule
              if (positions.length === 0) {
                continue;
              }
            } else {
              // Only if no context is provided, match just the selectedText
              // But limit to first occurrence to avoid matching all instances
              const firstPos = fullText.indexOf(rule.selectedText);
              if (firstPos !== -1) {
                positions = [firstPos];
              } else {
                continue; // No match found, skip this rule
              }
            }

            // Process matches in reverse order to avoid index shifting
            // Reverse order ensures we modify from end to start, preventing index issues
            positions.sort((a, b) => b - a);

            // Track which text nodes we've already modified to avoid conflicts
            const processedNodes = new Set<Text>();

            for (const position of positions) {
              // Position is already the start of selectedText in fullText (from our context matching)
              const selectedTextStart = position;
              const selectedTextEnd = selectedTextStart + rule.selectedText.length;

              // Find which text node contains the start of selectedText
              const selectedTextNode = nodeMap.find(
                (item) => selectedTextStart >= item.start && selectedTextStart < item.end
              );

              if (!selectedTextNode) continue;

              const textNode = selectedTextNode.node;
              if (!textNode.textContent) continue;

              // Skip if this node was already processed in this iteration
              if (processedNodes.has(textNode)) continue;

              // Check if this text node still exists and is valid
              if (!textNode.parentNode || textNode.parentElement?.closest('a')) {
                continue;
              }

              // Calculate relative positions within the text node
              const nodeRelativeStart = selectedTextStart - selectedTextNode.start;
              const nodeRelativeEnd = selectedTextEnd - selectedTextNode.start;

              // Check if selectedText fits within this single node
              if (nodeRelativeStart >= 0 && nodeRelativeEnd <= textNode.textContent.length) {
                // Extract text portions from single node
                const actualText = textNode.textContent.substring(nodeRelativeStart, nodeRelativeEnd);
                
                // Verify the selectedText matches (allow for slight variations due to whitespace)
                if (actualText === rule.selectedText || actualText.trim() === rule.selectedText.trim()) {
                  const beforeText = textNode.textContent.substring(0, nodeRelativeStart);
                  const selectedText = actualText;
                  const afterText = textNode.textContent.substring(nodeRelativeEnd);
                  
                  // Create link
                  const link = document.createElement('a');
                  link.href = rule.url;
                  link.textContent = selectedText;
                  link.className = 'interlink-link';
                  link.style.color = '#2563eb';
                  link.style.textDecoration = 'underline';
                  link.style.cursor = 'pointer';
                  if (rule.rel === 'nofollow') {
                    link.rel = 'nofollow';
                  }

                  const parent = textNode.parentNode;
                  if (parent) {
                    if (beforeText) {
                      parent.insertBefore(document.createTextNode(beforeText), textNode);
                    }
                    parent.insertBefore(link, textNode);
                    if (afterText) {
                      parent.insertBefore(document.createTextNode(afterText), textNode);
                    }
                    parent.removeChild(textNode);
                    processedNodes.add(textNode); // Mark as processed
                    foundAny = true;
                    hasChanges = true;
                    // Continue to next position instead of breaking - process all matches for this rule
                  }
                }
              }
              // If selectedText spans multiple nodes or doesn't match, continue to next position
            }
          }

          if (!foundAny) break;
          iteration++;
        }

        if (hasChanges) {
          injectedRef.current = true;
        }
      } catch (error) {
        console.error('Error injecting interlinks:', error);
      }
    };

    // Reset injection flag when rules change
    injectedRef.current = false;

    // Wait for page to be fully rendered
    const timeout = setTimeout(() => {
      injectLinks();
    }, 500);

    return () => clearTimeout(timeout);
  }, [rules, isLoading, isAdminRoute, isInterlinkingMode]);

  // Inject links with highlighting in interlinking mode (preview)
  useEffect(() => {
    if (!isInterlinkingMode || isAdminRoute) return;

    const fetchAndInjectPreviewLinks = async () => {
      try {
        // Remove previously injected preview links so we can re-inject without duplicating (no page reload)
        document.querySelectorAll('.interlink-link.interlink-preview').forEach((link) => {
          const text = document.createTextNode(link.textContent || '');
          link.parentNode?.replaceChild(text, link);
        });

        const response = await fetch(`/api/interlinking?path=${encodeURIComponent(pathname)}`);
        if (!response.ok) return;
        const data = await response.json();
        const previewRules = (data.rules || []).filter((rule: InterlinkRule) => rule.isActive);

        if (previewRules.length === 0) return;

        const main = document.querySelector('main');
        if (!main) return;

        // Helper function to get all text nodes
        const getAllTextNodes = () => {
          const walker = document.createTreeWalker(
            main!,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) => {
                const parent = node.parentElement;
                if (!parent) return NodeFilter.FILTER_REJECT;
                if (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
                  return NodeFilter.FILTER_REJECT;
                }
                if (parent.closest('a') || parent.closest('.interlink-link')) {
                  return NodeFilter.FILTER_REJECT;
                }
                // Allow interlinking inside FAQ (questions and answers) even when question is in a button
                if (parent.closest('[data-interlink-allow="faq"]')) {
                  return NodeFilter.FILTER_ACCEPT;
                }
                if (parent.closest('button') || parent.closest('[role="button"]') || parent.closest('[role="link"]')) {
                  return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
              },
            }
          );

          const textNodes: { node: Text; text: string }[] = [];
          let node: Text | null;
          while ((node = walker.nextNode() as Text | null)) {
            if (node && node.textContent) {
              textNodes.push({ node, text: node.textContent });
            }
          }
          return textNodes;
        };

        // Helper function to find all occurrences
        const findAllOccurrences = (text: string, pattern: string): number[] => {
          const occurrences: number[] = [];
          let index = text.indexOf(pattern);
          while (index !== -1) {
            occurrences.push(index);
            index = text.indexOf(pattern, index + 1);
          }
          return occurrences;
        };

        let iteration = 0;
        const maxIterations = 10;

        while (iteration < maxIterations) {
          const textNodes = getAllTextNodes();
          if (textNodes.length === 0) break;

          // Build text map
          let fullText = '';
          const nodeMap: Array<{ node: Text; start: number; end: number }> = [];
          textNodes.forEach(({ node, text }) => {
            const start = fullText.length;
            fullText += text;
            const end = fullText.length;
            nodeMap.push({ node, start, end });
          });

          let foundAny = false;

          // Process each rule
          for (const rule of previewRules) {
            let positions: number[] = [];

            // Check if context is provided
            const hasContextBefore = rule.contextBefore && rule.contextBefore.trim();
            const hasContextAfter = rule.contextAfter && rule.contextAfter.trim();
            const hasContext = hasContextBefore || hasContextAfter;
            
            if (hasContext) {
              // Build search pattern - account for potential whitespace between parts
              // We'll search for selectedText and verify context around it
              const selectedTextPositions = findAllOccurrences(fullText, rule.selectedText);
              
              // Filter positions where context matches
              for (const pos of selectedTextPositions) {
                const selectedTextEnd = pos + rule.selectedText.length;
                let contextMatches = true;
                
                // Check contextBefore (allow whitespace before contextBefore)
                if (hasContextBefore) {
                  // Look for contextBefore within reasonable distance before selectedText (up to 10 chars for whitespace)
                  const searchStart = Math.max(0, pos - rule.contextBefore.length - 10);
                  const textBeforeSelected = fullText.substring(searchStart, pos);
                  
                  // Try exact match first
                  let contextBeforeIndex = textBeforeSelected.lastIndexOf(rule.contextBefore);
                  
                  // If not found, try trimmed match (allows for whitespace)
                  if (contextBeforeIndex === -1) {
                    const trimmedBefore = textBeforeSelected.trim();
                    const trimmedContextBefore = rule.contextBefore.trim();
                    const trimmedIndex = trimmedBefore.lastIndexOf(trimmedContextBefore);
                    if (trimmedIndex !== -1) {
                      // Map back to original position accounting for trimmed whitespace
                      const beforeTrimmed = textBeforeSelected.substring(0, textBeforeSelected.indexOf(trimmedBefore));
                      contextBeforeIndex = beforeTrimmed.length + trimmedIndex;
                    }
                  }
                  
                  // Check if contextBefore is found and ends close to selectedText
                  if (contextBeforeIndex === -1) {
                    contextMatches = false;
                  } else {
                    const contextBeforeEnd = searchStart + contextBeforeIndex + rule.contextBefore.length;
                    // Allow up to 10 characters of whitespace/punctuation between contextBefore and selectedText
                    if (pos - contextBeforeEnd > 10) {
                      contextMatches = false;
                    }
                  }
                }
                
                // Check contextAfter (allow whitespace between selectedText and contextAfter)
                if (hasContextAfter && contextMatches) {
                  // Look for contextAfter within reasonable distance (up to 15 chars for whitespace/punctuation)
                  const searchEnd = Math.min(selectedTextEnd + rule.contextAfter.length + 15, fullText.length);
                  const textAfterSelected = fullText.substring(selectedTextEnd, searchEnd);
                  
                  // Try exact match first
                  let contextAfterIndex = textAfterSelected.indexOf(rule.contextAfter);
                  
                  // If not found, try trimmed match (allows for whitespace)
                  if (contextAfterIndex === -1) {
                    const trimmedAfter = textAfterSelected.trim();
                    const trimmedContextAfter = rule.contextAfter.trim();
                    const trimmedIndex = trimmedAfter.indexOf(trimmedContextAfter);
                    if (trimmedIndex !== -1 && trimmedIndex <= 10) {
                      // Find the position in original text accounting for trimmed whitespace
                      const beforeTrimmed = textAfterSelected.substring(0, textAfterSelected.indexOf(trimmedAfter));
                      contextAfterIndex = beforeTrimmed.length + trimmedIndex;
                    }
                  }
                  
                  // For very short contextAfter (like "?"), be more lenient
                  const maxDistance = rule.contextAfter.length <= 2 ? 5 : 15;
                  
                  // Check if contextAfter is found within reasonable distance
                  if (contextAfterIndex === -1 || contextAfterIndex > maxDistance) {
                    contextMatches = false;
                  }
                }
                
                if (contextMatches) {
                  positions.push(pos);
                }
              }
              
              // If no matches found with context, skip this rule
              if (positions.length === 0) {
                continue;
              }
            } else {
              // Only if no context is provided, match just the selectedText
              // But limit to first occurrence to avoid matching all instances
              const firstPos = fullText.indexOf(rule.selectedText);
              if (firstPos !== -1) {
                positions = [firstPos];
              } else {
                continue; // No match found, skip this rule
              }
            }

            positions.sort((a, b) => b - a);

            // Track which text nodes we've already modified to avoid conflicts
            const processedNodes = new Set<Text>();

            for (const position of positions) {
              // Position is already the start of selectedText in fullText (from our context matching)
              const selectedTextStart = position;
              const selectedTextEnd = selectedTextStart + rule.selectedText.length;

              // Find which text node contains the start of selectedText
              const selectedTextNode = nodeMap.find(
                (item) => selectedTextStart >= item.start && selectedTextStart < item.end
              );

              if (!selectedTextNode) continue;

              const textNode = selectedTextNode.node;
              if (!textNode.textContent || !textNode.parentNode) continue;
              
              // Skip if this node was already processed in this iteration
              if (processedNodes.has(textNode)) continue;
              
              if (textNode.parentElement?.closest('a')) continue;

              // Calculate relative positions within the text node
              const nodeRelativeStart = selectedTextStart - selectedTextNode.start;
              const nodeRelativeEnd = selectedTextEnd - selectedTextNode.start;

              // Check if selectedText fits within this single node
              if (nodeRelativeStart >= 0 && nodeRelativeEnd <= textNode.textContent.length) {
                // Extract text portions from single node
                const actualText = textNode.textContent.substring(nodeRelativeStart, nodeRelativeEnd);
                
                // Verify the selectedText matches (allow for slight variations due to whitespace)
                if (actualText === rule.selectedText || actualText.trim() === rule.selectedText.trim()) {
                  const beforeText = textNode.textContent.substring(0, nodeRelativeStart);
                  const selectedText = actualText;
                  const afterText = textNode.textContent.substring(nodeRelativeEnd);

                  const link = document.createElement('a');
                  link.href = rule.url;
                  link.textContent = selectedText;
                  link.className = 'interlink-link interlink-preview';
                  link.setAttribute('data-tooltip', rule.url);
                  link.setAttribute('data-rule-id', rule._id);
                  link.style.color = '#2563eb';
                  link.style.textDecoration = 'underline';
                  link.style.cursor = 'pointer';
                  link.style.backgroundColor = 'rgba(37, 99, 235, 0.15)';
                  link.style.borderRadius = '2px';
                  link.style.padding = '1px 2px';
                  link.title = rule.url;
                  if (rule.rel === 'nofollow') {
                    link.rel = 'nofollow';
                  }

                  link.addEventListener('click', (e) => {
                    e.preventDefault();
                    const rect = link.getBoundingClientRect();
                    if (window.parent && window.parent !== window) {
                      window.parent.postMessage(
                        {
                          type: 'LINK_CLICKED',
                          url: rule.url,
                          text: selectedText,
                          ruleId: rule._id,
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                        },
                        '*'
                      );
                    }
                  });

                  const parent = textNode.parentNode;
                  if (parent) {
                    if (beforeText) {
                      parent.insertBefore(document.createTextNode(beforeText), textNode);
                    }
                    parent.insertBefore(link, textNode);
                    if (afterText) {
                      parent.insertBefore(document.createTextNode(afterText), textNode);
                    }
                    parent.removeChild(textNode);
                    processedNodes.add(textNode); // Mark as processed
                    foundAny = true;
                    // Continue to next position instead of breaking - process all matches for this rule
                  }
                }
              }
              // If selectedText spans multiple nodes or doesn't match, continue to next position
            }
          }

          if (!foundAny) break;
          iteration++;
        }
      } catch (error) {
        console.error('Error injecting preview interlinks:', error);
      }
    };

    const timeout = setTimeout(() => {
      fetchAndInjectPreviewLinks();
    }, 500);

    return () => clearTimeout(timeout);
  }, [isInterlinkingMode, isAdminRoute, pathname, previewRefreshTrigger]);

  // Handle text selection mode (for admin preview)
  useEffect(() => {
    if (!isInterlinkingMode || isAdminRoute) return;

    const handleSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) return;

      const selectedText = selection.toString().trim();
      if (!selectedText || selectedText.length < 2) return;

      // Get surrounding context (50 chars before and after)
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;

      let contextBefore = '';
      let contextAfter = '';

      try {
        // Get text before selection
        const beforeRange = range.cloneRange();
        beforeRange.setStart(container, 0);
        beforeRange.setEnd(range.startContainer, range.startOffset);
        const beforeText = beforeRange.toString();
        contextBefore = beforeText.slice(-50).trim();

        // Get text after selection
        const afterRange = range.cloneRange();
        afterRange.setStart(range.endContainer, range.endOffset);
        const walker = document.createTreeWalker(
          afterRange.endContainer,
          NodeFilter.SHOW_TEXT
        );
        let afterText = '';
        let node: Node | null = afterRange.endContainer;
        let charCount = 0;
        const maxChars = 50;

        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const startOffset = afterRange.startOffset;
          afterText += text.substring(startOffset);
          charCount += text.length - startOffset;
        }

        while (charCount < maxChars && (node = walker.nextNode())) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent) {
            afterText += node.textContent;
            charCount += node.textContent.length;
          }
        }

        contextAfter = afterText.slice(0, maxChars).trim();
      } catch (error) {
        console.error('Error getting context:', error);
      }

      // Send message to parent (admin page)
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'TEXT_SELECTED',
            text: selectedText,
            contextBefore,
            contextAfter,
            path: pathname,
          },
          '*'
        );
      }

      // Clear selection
      selection.removeAllRanges();
    };

    // Listen for mouseup events (when user finishes selecting)
    const handleMouseUp = () => {
      setTimeout(handleSelection, 100);
    };

    document.addEventListener('mouseup', handleMouseUp);
    selectionListenerRef.current = () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };

    return () => {
      if (selectionListenerRef.current) {
        selectionListenerRef.current();
      }
    };
  }, [isInterlinkingMode, isAdminRoute, pathname]);

  // Reset injection flag when pathname changes
  useEffect(() => {
    injectedRef.current = false;
  }, [pathname]);

  return null; // This component doesn't render anything
}

